import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Building2,
  User,
  Mail,
  Phone,
  Loader2,
  Search,
  Stethoscope,
  Users,
  CreditCard,
  FileText,
  FlaskConical,
  Settings,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminClinicas() {
  const [clinicas, setClinicas] = useState([]);
  const [veterinarios, setVeterinarios] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showClinicaDialog, setShowClinicaDialog] = useState(false);
  const [showVetDialog, setShowVetDialog] = useState(false);
  const [editingClinica, setEditingClinica] = useState(null);
  const [editingVet, setEditingVet] = useState(null);
  const [selectedClinica, setSelectedClinica] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userPermisos, setUserPermisos] = useState(null);
  const navigate = useNavigate();

  const [clinicaForm, setClinicaForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    password: '',
    forma_cobro: 'cotizacion',
    grupos_permitidos: [],
    mostrar_cultivos: true,
    limite_credito_habilitado: false,
    limite_credito: '',
  });

  const [vetForm, setVetForm] = useState({
    nombre: '',
    email: '',
  });

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Permission helpers
  const canCreate = () => userPermisos?.clinicas?.includes('crear');
  const canEdit = () => userPermisos?.clinicas?.includes('editar');
  const canDelete = () => userPermisos?.clinicas?.includes('eliminar');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUserPermisos();
    fetchData();
  }, [token, navigate]);

  const fetchUserPermisos = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/me`, { headers });
      setUserPermisos(response.data.permisos);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [clinicasRes, vetsRes, gruposRes] = await Promise.all([
        axios.get(`${API}/admin/clinicas`, { headers }),
        axios.get(`${API}/admin/veterinarios`, { headers }),
        axios.get(`${API}/catalogo/grupos`),
      ]);
      setClinicas(clinicasRes.data);
      setVeterinarios(vetsRes.data);
      setGrupos(gruposRes.data);

      const activeClinicas = clinicasRes.data.filter((c) => c.activo);
      if (activeClinicas.length > 0 && !selectedClinica) {
        setSelectedClinica(activeClinicas[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClinica = async () => {
    if (!clinicaForm.nombre || !clinicaForm.email || !clinicaForm.telefono) {
      toast.error('Nombre, email y teléfono son requeridos');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clinicaForm.email)) {
      toast.error('Por favor ingrese un email válido');
      return;
    }

    if (!editingClinica && !clinicaForm.password) {
      toast.error('La contraseña es requerida para nuevas clínicas');
      return;
    }

    setIsSaving(true);
    try {
      const dataToSend = {
        ...clinicaForm,
        limite_credito: clinicaForm.limite_credito ? parseFloat(clinicaForm.limite_credito) : 0,
      };
      
      if (editingClinica) {
        const updateData = { ...dataToSend };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/admin/clinicas/${editingClinica.id}`, updateData, { headers });
        toast.success('Clínica actualizada');
      } else {
        await axios.post(`${API}/admin/clinicas`, dataToSend, { headers });
        toast.success('Clínica creada');
      }
      setShowClinicaDialog(false);
      resetClinicaForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || 'Error de validación').join(', ');
        toast.error(messages);
      } else {
        toast.error(detail || 'Error al guardar');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClinica = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Está seguro de desactivar esta clínica?')) return;

    try {
      await axios.delete(`${API}/admin/clinicas/${id}`, { headers });
      toast.success('Clínica desactivada');
      if (selectedClinica?.id === id) {
        setSelectedClinica(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const handleSaveVet = async () => {
    if (!vetForm.nombre || !vetForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(vetForm.email)) {
      toast.error('Por favor ingrese un email válido');
      return;
    }

    setIsSaving(true);
    try {
      if (editingVet) {
        await axios.put(`${API}/admin/veterinarios/${editingVet.id}`, vetForm, { headers });
        toast.success('Veterinario actualizado');
      } else {
        await axios.post(
          `${API}/admin/veterinarios`,
          { ...vetForm, clinica_id: selectedClinica.id },
          { headers }
        );
        toast.success('Veterinario creado');
      }
      setShowVetDialog(false);
      resetVetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects)
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || 'Error de validación').join(', ');
        toast.error(messages);
      } else {
        toast.error(detail || 'Error al guardar');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteVet = async (id) => {
    if (!window.confirm('¿Está seguro de desactivar este veterinario?')) return;

    try {
      await axios.delete(`${API}/admin/veterinarios/${id}`, { headers });
      toast.success('Veterinario desactivado');
      fetchData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const resetClinicaForm = () => {
    setClinicaForm({ nombre: '', email: '', telefono: '', password: '', forma_cobro: 'cotizacion', grupos_permitidos: [], mostrar_cultivos: true, limite_credito_habilitado: false, limite_credito: '' });
    setEditingClinica(null);
  };

  const resetVetForm = () => {
    setVetForm({ nombre: '', email: '' });
    setEditingVet(null);
  };

  const openEditClinica = (clinica, e) => {
    e.stopPropagation();
    setEditingClinica(clinica);
    setClinicaForm({
      nombre: clinica.nombre,
      email: clinica.email,
      telefono: clinica.telefono,
      password: '',
      forma_cobro: clinica.forma_cobro || 'cotizacion',
      grupos_permitidos: clinica.grupos_permitidos || [],
      mostrar_cultivos: clinica.mostrar_cultivos !== false,
      limite_credito_habilitado: clinica.limite_credito_habilitado || false,
      limite_credito: clinica.limite_credito || '',
    });
    setShowClinicaDialog(true);
  };

  const openAddVet = () => {
    if (!selectedClinica) {
      toast.error('Seleccione una clínica primero');
      return;
    }
    setShowVetDialog(true);
  };

  const openEditVet = (vet) => {
    setEditingVet(vet);
    setVetForm({ nombre: vet.nombre, email: vet.email });
    setShowVetDialog(true);
  };

  const getVetsForClinica = (clinicaId) => {
    let filtered = veterinarios.filter((v) => v.clinica_id === clinicaId && v.activo);
    if (searchTerm) {
      filtered = filtered.filter(
        (v) =>
          v.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  const activeClinicas = clinicas.filter((c) => c.activo);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#505BB3] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="h-10 border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-900">Gestión de Clínicas</h1>
                <p className="text-sm text-slate-500">Administra clínicas y veterinarios</p>
              </div>
            </div>
            {canCreate() && (
              <Button
                onClick={() => setShowClinicaDialog(true)}
                className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Clínica
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Master-Detail Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Clinics List */}
        <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
          {/* Panel Header */}
          <div className="bg-[#505BB3] px-4 py-3">
            <div className="flex items-center gap-2 text-white">
              <Building2 className="w-5 h-5" />
              <span className="font-semibold">Clínicas</span>
              <span className="ml-auto text-sm bg-white/20 px-2 py-0.5 rounded-full">
                {activeClinicas.length}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {activeClinicas.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">No hay clínicas registradas</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {activeClinicas.map((clinica) => (
                  <div
                    key={clinica.id}
                    onClick={() => setSelectedClinica(clinica)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedClinica?.id === clinica.id
                        ? 'bg-[#505BB3]/5 border-l-4 border-[#505BB3]'
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedClinica?.id === clinica.id
                              ? 'bg-[#505BB3] text-white'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`font-medium truncate ${
                              selectedClinica?.id === clinica.id
                                ? 'text-[#505BB3]'
                                : 'text-slate-900'
                            }`}
                          >
                            {clinica.nombre}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{clinica.email}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {getVetsForClinica(clinica.id).length} vets
                            </p>
                            {clinica.forma_cobro === 'cotizacion' && clinica.saldo_pendiente > 0 && (
                              <p className="text-xs text-amber-600 font-medium">
                                ${clinica.saldo_pendiente.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        {canEdit() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-slate-200"
                            onClick={(e) => openEditClinica(clinica, e)}
                          >
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                        {canDelete() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-red-100"
                            onClick={(e) => handleDeleteClinica(clinica.id, e)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Veterinarians List */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedClinica ? (
            <>
              {/* Clinic Detail Header */}
              <div className="bg-white border-b border-slate-200 p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#505BB3]/10 rounded-xl flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-[#505BB3]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{selectedClinica.nombre}</h2>
                      <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {selectedClinica.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {selectedClinica.telefono}
                        </span>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedClinica.forma_cobro === 'stripe' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {selectedClinica.forma_cobro === 'stripe' ? (
                            <><CreditCard className="w-3 h-3" /> Stripe</>
                          ) : (
                            <><FileText className="w-3 h-3" /> Cotización</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  {canCreate() && (
                    <Button
                      onClick={openAddVet}
                      className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Veterinario
                    </Button>
                  )}
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar veterinarios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-10 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                  />
                </div>
              </div>

              {/* Veterinarians Table */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-[#505BB3] px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                      <Stethoscope className="w-5 h-5" />
                      <span className="font-semibold">Veterinarios</span>
                      <span className="ml-auto text-sm bg-white/20 px-2 py-0.5 rounded-full">
                        {getVetsForClinica(selectedClinica.id).length}
                      </span>
                    </div>
                  </div>

                  {getVetsForClinica(selectedClinica.id).length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Stethoscope className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500">
                        {searchTerm
                          ? 'No se encontraron veterinarios'
                          : 'No hay veterinarios en esta clínica'}
                      </p>
                      {!searchTerm && (
                        <Button
                          onClick={openAddVet}
                          variant="outline"
                          className="mt-4 border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Agregar Veterinario
                        </Button>
                      )}
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                            Nombre
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                            Email
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-24">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {getVetsForClinica(selectedClinica.id).map((vet) => (
                          <tr key={vet.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <User className="w-4 h-4 text-slate-500" />
                                </div>
                                <span className="font-medium text-slate-900">{vet.nombre}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-600 flex items-center gap-1">
                                <Mail className="w-4 h-4 text-slate-400" />
                                {vet.email}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                {canEdit() && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-slate-200"
                                    onClick={() => openEditVet(vet)}
                                  >
                                    <Edit className="w-4 h-4 text-slate-500" />
                                  </Button>
                                )}
                                {canDelete() && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-red-100"
                                    onClick={() => handleDeleteVet(vet.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500">Seleccione una clínica para ver sus veterinarios</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clinica Dialog */}
      <Dialog
        open={showClinicaDialog}
        onOpenChange={(open) => {
          if (!open) resetClinicaForm();
          setShowClinicaDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {editingClinica ? 'Editar Clínica' : 'Nueva Clínica'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Nombre *</Label>
              <Input
                value={clinicaForm.nombre}
                onChange={(e) => setClinicaForm({ ...clinicaForm, nombre: e.target.value })}
                placeholder="Nombre de la clínica"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Email *</Label>
              <Input
                type="email"
                value={clinicaForm.email}
                onChange={(e) => setClinicaForm({ ...clinicaForm, email: e.target.value })}
                placeholder="clinica@ejemplo.com"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Teléfono *</Label>
              <Input
                value={clinicaForm.telefono}
                onChange={(e) => setClinicaForm({ ...clinicaForm, telefono: e.target.value })}
                placeholder="(555) 123-4567"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                {editingClinica ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}
              </Label>
              <Input
                type="password"
                value={clinicaForm.password}
                onChange={(e) => setClinicaForm({ ...clinicaForm, password: e.target.value })}
                placeholder="••••••••"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Forma de Cobro</Label>
              <Select
                value={clinicaForm.forma_cobro}
                onValueChange={(value) => setClinicaForm({ ...clinicaForm, forma_cobro: value })}
              >
                <SelectTrigger className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]">
                  <SelectValue placeholder="Seleccione forma de cobro" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cotizacion">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      <span>Solo Cotización (Crédito)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="stripe">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-green-500" />
                      <span>Cobrar con Stripe</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {clinicaForm.forma_cobro === 'stripe' 
                  ? 'La clínica deberá pagar al enviar cada solicitud'
                  : 'La clínica recibirá cotización sin cobro automático'}
              </p>
            </div>

            {/* Grupos de Pruebas Permitidos */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <FlaskConical className="w-4 h-4" />
                Grupos de Pruebas Disponibles
              </Label>
              <p className="text-xs text-slate-500">
                Si no selecciona ninguno, se mostrarán todos los grupos
              </p>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
                {grupos.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">No hay grupos disponibles</p>
                ) : (
                  grupos.map((grupo) => (
                    <label
                      key={grupo.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={clinicaForm.grupos_permitidos.includes(grupo.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setClinicaForm({
                              ...clinicaForm,
                              grupos_permitidos: [...clinicaForm.grupos_permitidos, grupo.id]
                            });
                          } else {
                            setClinicaForm({
                              ...clinicaForm,
                              grupos_permitidos: clinicaForm.grupos_permitidos.filter(id => id !== grupo.id)
                            });
                          }
                        }}
                        className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                      />
                      <span className="text-sm text-slate-700">{grupo.nombre}</span>
                    </label>
                  ))
                )}
              </div>
              {clinicaForm.grupos_permitidos.length > 0 && (
                <p className="text-xs text-[#505BB3] font-medium">
                  {clinicaForm.grupos_permitidos.length} grupo(s) seleccionado(s)
                </p>
              )}
            </div>

            {/* Mostrar Sección de Cultivos */}
            <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-slate-500" />
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Sección de Cultivos y Microscopía
                  </Label>
                  <p className="text-xs text-slate-500">
                    Mostrar esta sección en el formulario de solicitud
                  </p>
                </div>
              </div>
              <Switch
                checked={clinicaForm.mostrar_cultivos}
                onCheckedChange={(checked) => setClinicaForm({ ...clinicaForm, mostrar_cultivos: checked })}
              />
            </div>

            {/* Límite de Crédito - Solo para modo Cotización */}
            {clinicaForm.forma_cobro === 'cotizacion' && (
              <div className="space-y-4 p-4 border border-amber-200 rounded-lg bg-amber-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Habilitar Límite de Crédito
                      </Label>
                      <p className="text-xs text-slate-500">
                        Bloquear nuevas solicitudes si se excede el límite
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={clinicaForm.limite_credito_habilitado}
                    onCheckedChange={(checked) => setClinicaForm({ ...clinicaForm, limite_credito_habilitado: checked })}
                    data-testid="limite-credito-switch"
                  />
                </div>
                
                {clinicaForm.limite_credito_habilitado && (
                  <div className="space-y-2 pt-2 border-t border-amber-200">
                    <Label className="text-sm font-medium text-slate-700">Límite de Crédito (MXN)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={clinicaForm.limite_credito}
                        onChange={(e) => setClinicaForm({ ...clinicaForm, limite_credito: e.target.value })}
                        placeholder="0.00"
                        className="pl-8 h-11 border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                        data-testid="limite-credito-input"
                      />
                    </div>
                    <p className="text-xs text-amber-700">
                      Monto máximo de deuda permitido para esta clínica
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetClinicaForm();
                setShowClinicaDialog(false);
              }}
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveClinica}
              disabled={isSaving}
              className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingClinica ? 'Guardar Cambios' : 'Crear Clínica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Veterinario Dialog */}
      <Dialog
        open={showVetDialog}
        onOpenChange={(open) => {
          if (!open) resetVetForm();
          setShowVetDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {editingVet ? 'Editar Veterinario' : 'Nuevo Veterinario'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Nombre *</Label>
              <Input
                value={vetForm.nombre}
                onChange={(e) => setVetForm({ ...vetForm, nombre: e.target.value })}
                placeholder="Dr. Juan Pérez"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Email *</Label>
              <Input
                type="email"
                value={vetForm.email}
                onChange={(e) => setVetForm({ ...vetForm, email: e.target.value })}
                placeholder="veterinario@ejemplo.com"
                className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetVetForm();
                setShowVetDialog(false);
              }}
              className="border-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveVet}
              disabled={isSaving}
              className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingVet ? 'Guardar Cambios' : 'Crear Veterinario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
